import { useLang } from "@/contexts/LanguageContext";
import { LANGS } from "@/i18n/translations";

/**
 * Elegant compact welcome screen — Hood brand.
 * Mobile-first, minimalist, with dark gradient + Hood logo card + clear language pills.
 */
export default function LanguageChooser({ forceOpen = false, onClose }) {
  const { showChooser, setShowChooser, setLang, t, lang: currentLang } = useLang();
  const visible = forceOpen || showChooser;
  if (!visible) return null;

  const handlePick = (code) => {
    setLang(code);
    setShowChooser(false);
    if (onClose) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-5 overflow-hidden"
      data-testid="language-chooser"
      style={{
        background: "linear-gradient(160deg, #041712 0%, #0C2E20 50%, #041712 100%)",
      }}
    >
      {/* Soft light beam */}
      <div
        className="pointer-events-none absolute top-1/4 left-1/2 -translate-x-1/2 w-[420px] h-[420px] rounded-full blur-3xl opacity-40"
        style={{ background: "radial-gradient(circle, #1DCF75 0%, transparent 65%)" }}
      />

      <style>{`
        @keyframes hood-in {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes hood-scale {
          from { opacity: 0; transform: scale(.9); }
          to   { opacity: 1; transform: scale(1); }
        }
        .hood-in    { animation: hood-in .6s cubic-bezier(.2,.7,.3,1) both; }
        .hood-scale { animation: hood-scale .7s cubic-bezier(.2,.9,.3,1) both; }
      `}</style>

      <div className="relative z-10 max-w-sm w-full">
        {/* Logo tile */}
        <div className="hood-scale flex justify-center mb-6">
          <div
            className="relative rounded-[28px] p-3"
            style={{
              background: "linear-gradient(145deg, rgba(29,207,117,0.15), rgba(29,207,117,0.02))",
              border: "1px solid rgba(29,207,117,0.25)",
              boxShadow: "0 20px 60px -20px rgba(29,207,117,0.35), inset 0 0 30px rgba(29,207,117,0.06)",
            }}
          >
            <img
              src="/hood-icon.png"
              alt="Hood"
              className="w-24 h-24 rounded-[22px] object-cover"
              data-testid="welcome-logo"
            />
          </div>
        </div>

        {/* Title block */}
        <div className="hood-in text-center mb-1" style={{ animationDelay: ".15s" }}>
          <h1 className="text-white text-4xl font-black tracking-tight" data-testid="welcome-title">
            هود
          </h1>
        </div>
        <div className="hood-in text-center mb-1" style={{ animationDelay: ".22s" }}>
          <span className="text-emerald-300/80 text-sm font-bold tracking-[0.5em]">HOOD</span>
        </div>
        <p className="hood-in text-center text-white/50 text-xs mb-8" style={{ animationDelay: ".3s" }}>
          دليل مدينتك الذكي · Your smart city guide
        </p>

        {/* Prompt */}
        <div className="hood-in text-center mb-3" style={{ animationDelay: ".38s" }}>
          <p className="text-white/85 text-sm font-bold">اختر لغتك المفضلة</p>
          <p className="text-white/40 text-[11px] mt-0.5">Choose your language · زمانی خۆت هەڵبژێرە</p>
        </div>

        {/* Language pills */}
        <div className="hood-in space-y-2" style={{ animationDelay: ".46s" }}>
          {Object.values(LANGS).map((L) => {
            const active = currentLang === L.code;
            return (
              <button
                key={L.code}
                onClick={() => handlePick(L.code)}
                data-testid={`lang-pick-${L.code}`}
                lang={L.code}
                className={`group w-full py-3.5 px-4 rounded-2xl border transition-all duration-200 flex items-center gap-3 text-start active:scale-[0.97] ${
                  active
                    ? "bg-emerald-500 border-emerald-300 text-emerald-950 shadow-[0_10px_25px_-10px_rgba(29,207,117,0.7)]"
                    : "bg-white/5 border-white/10 text-white hover:border-emerald-400/60 hover:bg-white/10"
                }`}
                dir={L.dir}
              >
                <span className="text-2xl">{L.flag}</span>
                <div className="flex-1">
                  <div className="text-base font-black leading-none">{L.native}</div>
                  <div className={`text-[10px] mt-1 ${active ? "text-emerald-950/70" : "text-white/45"}`}>
                    {L.name}
                  </div>
                </div>
                <span className={active ? "text-lg" : "text-lg opacity-30 group-hover:opacity-70 transition"}>
                  {active ? "✓" : "→"}
                </span>
              </button>
            );
          })}
        </div>

        {forceOpen && (
          <button
            onClick={() => { setShowChooser(false); if (onClose) onClose(); }}
            className="mt-5 w-full text-white/50 hover:text-white text-sm font-semibold transition-colors"
            data-testid="lang-chooser-close"
          >
            {t("common.close")}
          </button>
        )}

        {/* Bottom brand tag */}
        <div className="hood-in mt-8 flex items-center justify-center gap-2 text-white/25" style={{ animationDelay: ".55s" }}>
          <span className="size-1 rounded-full bg-emerald-400" />
          <span className="text-[10px] tracking-[0.35em] uppercase">Hood</span>
          <span className="size-1 rounded-full bg-emerald-400" />
        </div>
      </div>
    </div>
  );
}
